import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Download, LogOut } from "lucide-react";
import { PawMark } from "@/components/PawMark";
import { Stars } from "@/components/Reviews";
import { PRODUCTS } from "@/data/catalog";
import { KeeperJournal } from "@/components/KeeperJournal";
import { KeeperOrders } from "@/components/KeeperOrders";
import { KeeperBirthdays } from "@/components/KeeperBirthdays";
import { KeeperSales } from "@/components/KeeperSales";
import { KeeperCheckins } from "@/components/KeeperCheckins";
import { KeeperLetters } from "@/components/KeeperLetters";
import { KeeperBatches } from "@/components/KeeperBatches";
import { KeeperChats } from "@/components/KeeperChats";
import { KeeperFaq, KeeperSettings } from "@/components/KeeperSettings";
import { ShipCount } from "@/components/ShipCount";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "dp_keeper_token";
const productName = (id) => PRODUCTS.find((p) => p.id === id)?.name || id;
const errText = (e) => (typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Something went wrong.");

const Login = ({ onToken }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/keeper/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      onToken(data.token);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="max-w-sm mx-auto mt-24 text-center" data-testid="keeper-login-form">
      <PawMark className="w-10 h-10 mx-auto text-[#B5A642]" />
      <h1 className="mt-5 font-display font-light text-4xl text-[#E8DDC7]">The Keeper's Desk</h1>
      <p className="mt-2 text-[13px] text-[#D3CEC4]/50">For the keepers of the house only.</p>
      <input type="password" required data-testid="keeper-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
        className="mt-8 w-full bg-transparent border border-[#303B2D] rounded-sm px-4 py-3 text-[#E8DDC7] focus:outline-none focus:border-[#B5A642]/70" />
      {error && <p data-testid="keeper-login-error" className="mt-3 text-[12px] text-[#C9876B]">{error}</p>}
      <button data-testid="keeper-login-submit" disabled={busy} className="mt-4 w-full py-3 bg-[#4B3226] text-[#E8DDC7] text-[11px] uppercase tracking-[0.2em] rounded-sm hover:brightness-125 transition-[filter] disabled:opacity-50">
        {busy ? "Unlocking…" : "Unlock the desk"}
      </button>
    </form>
  );
};

const STATUS_STYLE = { pending: "text-[#C9A96B]", approved: "text-[#8FB08A]", hidden: "text-[#D3CEC4]/40" };

const ReviewRow = ({ r, onSet }) => (
  <li className="py-5 border-b border-[#303B2D]/60" data-testid={`keeper-review-${r.id}`}>
    <div className="flex flex-wrap items-center gap-3">
      <Stars value={r.rating} />
      <span className="font-display text-lg text-[#E8DDC7]">{productName(r.product_id)}</span>
      <span className={`text-[10px] uppercase tracking-[0.2em] ${STATUS_STYLE[r.status]}`} data-testid={`keeper-review-status-${r.id}`}>{r.status}</span>
      <span className="ml-auto font-ledger text-[11px] text-[#D3CEC4]/40">{new Date(r.created_at).toLocaleDateString("en-CA")}</span>
    </div>
    <p className="mt-2 text-[14px] text-[#D3CEC4]/85 leading-relaxed">“{r.note}”</p>
    <p className="mt-1 text-[12px] text-[#D3CEC4]/50">
      {r.first_name}{r.dog_name && ` · ${r.dog_name}`}{r.dog_breed && `, ${r.dog_breed}`} · {r.email}
    </p>
    <div className="mt-3 flex gap-2">
      {r.status !== "approved" && (
        <button data-testid={`keeper-approve-${r.id}`} onClick={() => onSet(r.id, "approved")} className="px-4 py-1.5 border border-[#8FB08A]/60 text-[11px] uppercase tracking-[0.15em] text-[#8FB08A] rounded-sm hover:bg-[#8FB08A]/10 transition-colors">Approve</button>
      )}
      {r.status !== "hidden" && (
        <button data-testid={`keeper-hide-${r.id}`} onClick={() => onSet(r.id, "hidden")} className="px-4 py-1.5 border border-[#303B2D] text-[11px] uppercase tracking-[0.15em] text-[#D3CEC4]/60 rounded-sm hover:border-[#C9876B]/60 transition-colors">Hide</button>
      )}
    </div>
  </li>
);

const Desk = ({ token, onLogout }) => {
  const [tab, setTab] = useState("orders");
  const [refreshKey, setRefreshKey] = useState(0);
  const [ordersFilter, setOrdersFilter] = useState("all");
  const [ordersKey, setOrdersKey] = useState(0);
  const showToShip = () => {
    setOrdersFilter("to_ship");
    setOrdersKey((k) => k + 1);
    setTab("orders");
  };
  const [reviews, setReviews] = useState([]);
  const [subs, setSubs] = useState([]);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    try {
      const [r, s] = await Promise.all([axios.get(`${API}/keeper/reviews`, auth), axios.get(`${API}/keeper/subscribers`, auth)]);
      setReviews(r.data);
      setSubs(s.data);
    } catch (e) {
      if (e.response?.status === 401) onLogout();
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await axios.post(`${API}/keeper/reviews/${id}`, { status }, auth);
    load();
  };

  const downloadCsv = async () => {
    const { data } = await axios.get(`${API}/keeper/subscribers.csv`, { ...auth, responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = Object.assign(document.createElement("a"), { href: url, download: "apothecary-correspondence.csv" });
    a.click();
    URL.revokeObjectURL(url);
  };

  const pending = reviews.filter((r) => r.status === "pending").length;
  const tabClass = (k) => `pb-2 text-[11px] uppercase tracking-[0.25em] border-b transition-colors ${tab === k ? "text-[#B5A642] border-[#B5A642]" : "text-[#D3CEC4]/50 border-transparent hover:text-[#E8DDC7]"}`;

  return (
    <div className="max-w-6xl mx-auto px-5 py-16" data-testid="keeper-desk">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-light text-4xl text-[#E8DDC7]">The Keeper's Desk</h1>
        <button data-testid="keeper-logout" onClick={onLogout} className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#D3CEC4]/50 hover:text-[#E8DDC7] transition-colors">
          <LogOut className="w-3.5 h-3.5" /> Lock
        </button>
      </div>
      <div className="mt-6"><ShipCount token={token} refreshKey={refreshKey} onClick={showToShip} /></div>
      <div className="mt-10 flex flex-wrap gap-8">
        <button data-testid="keeper-tab-sales" onClick={() => setTab("sales")} className={tabClass("sales")}>Sales</button>
        <button data-testid="keeper-tab-orders" onClick={() => setTab("orders")} className={tabClass("orders")}>Orders</button>
        <button data-testid="keeper-tab-reviews" onClick={() => setTab("reviews")} className={tabClass("reviews")}>Notes · {pending} awaiting</button>
        <button data-testid="keeper-tab-subscribers" onClick={() => setTab("subscribers")} className={tabClass("subscribers")}>Correspondence · {subs.length}</button>
        <button data-testid="keeper-tab-journal" onClick={() => setTab("journal")} className={tabClass("journal")}>The Journal</button>
        <button data-testid="keeper-tab-birthdays" onClick={() => setTab("birthdays")} className={tabClass("birthdays")}>Birthdays</button>
        <button data-testid="keeper-tab-checkins" onClick={() => setTab("checkins")} className={tabClass("checkins")}>Check-ins</button>
        <button data-testid="keeper-tab-letters" onClick={() => setTab("letters")} className={tabClass("letters")}>Seasonal letters</button>
        <button data-testid="keeper-tab-batches" onClick={() => setTab("batches")} className={tabClass("batches")}>Batch QR codes</button>
        <button data-testid="keeper-tab-chats" onClick={() => setTab("chats")} className={tabClass("chats")}>Shopper questions</button>
        <button data-testid="keeper-tab-faq" onClick={() => setTab("faq")} className={tabClass("faq")}>FAQ drafts</button>
        <button data-testid="keeper-tab-settings" onClick={() => setTab("settings")} className={tabClass("settings")}>Shop settings</button>
      </div>
      {tab === "reviews" && (
        reviews.length ? <ul className="mt-4">{reviews.map((r) => <ReviewRow key={r.id} r={r} onSet={setStatus} />)}</ul>
          : <p data-testid="keeper-reviews-empty" className="mt-8 font-display italic text-[#D3CEC4]/50">No notes have arrived yet.</p>
      )}
      {tab === "orders" && <KeeperOrders key={ordersKey} token={token} initialFilter={ordersFilter} onChange={() => setRefreshKey((k) => k + 1)} />}
      {tab === "journal" && <KeeperJournal token={token} />}
      {tab === "birthdays" && <KeeperBirthdays token={token} />}
      {tab === "sales" && <KeeperSales token={token} />}
      {tab === "checkins" && <KeeperCheckins token={token} />}
      {tab === "letters" && <KeeperLetters token={token} />}
      {tab === "batches" && <KeeperBatches token={token} />}
      {tab === "chats" && <KeeperChats token={token} />}
      {tab === "faq" && <KeeperFaq token={token} />}
      {tab === "settings" && <KeeperSettings token={token} />}
      {tab === "subscribers" && (
        <div className="mt-6">
          <button data-testid="keeper-download-csv" onClick={downloadCsv} className="inline-flex items-center gap-2 px-4 py-2 border border-[#B5A642]/60 text-[11px] uppercase tracking-[0.2em] text-[#D3CEC4] rounded-sm hover:bg-[#B5A642]/10 transition-colors">
            <Download className="w-3.5 h-3.5" /> Download CSV
          </button>
          <table className="mt-6 w-full text-left text-[13px]" data-testid="keeper-subscribers-table">
            <thead className="text-[10px] uppercase tracking-[0.2em] text-[#D3CEC4]/45">
              <tr><th className="py-2">Email</th><th>Status</th><th>Lang</th><th>Code</th><th>Joined</th></tr>
            </thead>
            <tbody>
              {subs.map((s) => (
                <tr key={s.email} className="border-t border-[#303B2D]/60 text-[#D3CEC4]/80">
                  <td className="py-2.5">{s.email}</td>
                  <td>{s.status}</td>
                  <td>{s.lang}</td>
                  <td className="font-ledger">{s.code}{s.redeemed && <span className="text-[#D3CEC4]/40"> · used</span>}</td>
                  <td className="font-ledger text-[#D3CEC4]/50">{new Date(s.created_at).toLocaleDateString("en-CA")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default function Keeper() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  useEffect(() => {
    document.title = "The Keeper's Desk — Dirty Paws Apothecary";
    const m = Object.assign(document.createElement("meta"), { name: "robots", content: "noindex" });
    document.head.appendChild(m);
    return () => m.remove();
  }, []);
  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };
  return (
    <div className="min-h-screen bg-[#1C1F1E]" data-no-translate>
      <div className="px-5 pt-6"><Link to="/" data-testid="keeper-home-link" className="text-[11px] uppercase tracking-[0.2em] text-[#D3CEC4]/50 hover:text-[#E8DDC7]">← The house</Link></div>
      {token ? <Desk token={token} onLogout={logout} /> : <Login onToken={setToken} />}
    </div>
  );
}
